import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { Globe, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SUPPORTED_LANGUAGES, SupportedLanguage, getLocalizedPath, removeLanguagePrefix } from "@/i18n/config";
import { cn } from "@/lib/utils";

interface LanguageSelectorProps {
  variant?: "header" | "footer" | "minimal";
  showLabel?: boolean;
  className?: string;
}

export function LanguageSelector({ 
  variant = "header", 
  showLabel = true,
  className 
}: LanguageSelectorProps) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const handleLanguageChange = (langCode: SupportedLanguage) => {
    // Update i18n
    i18n.changeLanguage(langCode);
    
    // Get current path without language prefix
    const currentPath = removeLanguagePrefix(location.pathname);
    
    // Navigate to the new localized path
    const newPath = getLocalizedPath(currentPath, langCode);
    navigate(newPath, { replace: true });
    
    setIsOpen(false);
  };

  if (variant === "minimal") {
    return (
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn("gap-1.5 text-foreground/70 hover:text-foreground", className)}
          >
            <span className="text-base">{currentLanguage.flag}</span>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[140px]">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <DropdownMenuItem
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code as SupportedLanguage)}
              className={cn(
                "flex items-center gap-2 cursor-pointer",
                lang.code === i18n.language && "bg-primary/10"
              )}
            >
              <span className="text-base">{lang.flag}</span>
              <span className="flex-1">{lang.nativeName}</span>
              {lang.code === i18n.language && (
                <Check className="h-4 w-4 text-primary" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (variant === "footer") {
    return (
      <div className={cn("flex flex-col gap-2", className)}>
        {showLabel && (
          <span className="text-sm text-background/50 uppercase tracking-wider">
            {t("language.select")}
          </span>
        )}
        <div className="flex flex-wrap gap-2">
          {SUPPORTED_LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code as SupportedLanguage)}
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors",
                lang.code === i18n.language
                  ? "bg-primary text-primary-foreground"
                  : "bg-background/10 text-background/70 hover:bg-background/20 hover:text-background"
              )}
            >
              <span>{lang.flag}</span>
              <span>{lang.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Default header variant
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn("gap-2 text-foreground/70 hover:text-primary hover:bg-accent", className)}
        >
          <Globe className="h-4 w-4" />
          {showLabel && (
            <>
              <span className="hidden sm:inline">{currentLanguage.nativeName}</span>
              <span className="sm:hidden">{currentLanguage.flag}</span>
            </>
          )}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[160px]">
        {SUPPORTED_LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code as SupportedLanguage)}
            className={cn(
              "flex items-center gap-3 cursor-pointer",
              lang.code === i18n.language && "bg-primary/10"
            )}
          >
            <span className="text-lg">{lang.flag}</span>
            <span className="flex-1 font-medium">{lang.nativeName}</span>
            {lang.code === i18n.language && (
              <Check className="h-4 w-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
